import {
  ExpressionNodeQueryType,
  PluginTrafficMatchResponse,
} from "@intelligems/ig-types";
import { Css, fromProcess } from "@intelligems/ig-utils";

export const injectStyles = (styles: string) => {
  const style = document.createElement("style");
  style.innerHTML = styles;
  document.head.appendChild(style);
};

export const setStyles = <T>(
  prefix: string,
  desktopStyles?: T | any,
  mobileStyles?: T | any
) => {
  for (const [key, value] of Object.entries(desktopStyles || {})) {
    if (typeof value === "object") {
      // @ts-ignore
      for (const [section, sectionValue] of Object.entries(value) || {}) {
        Css.setCssVariable(
          `${prefix}-${key}-${section}-d`,
          sectionValue as string
        );
        Css.setCssVariable(
          `${prefix}-${key}-${section}-m`,
          sectionValue as string
        );
      }
    }
  }

  for (const [key, value] of Object.entries(mobileStyles || {})) {
    if (typeof value === "object") {
      for (const [section, sectionValue] of Object.entries(value || {})) {
        Css.setCssVariable(
          `${prefix}-${key}-${section}-m`,
          sectionValue as string
        );
      }
    }
  }
};

export async function HMAC(message: string, key: string) {
  if (fromProcess("NODE_ENV", undefined) === "test") {
    const Base64 = await import("crypto-js/enc-base64");
    const HmacSHA512 = await import("crypto-js/hmac-sha512");
    const bodyStr = message;
    const hmac = HmacSHA512.default(bodyStr, key);
    return Base64.default.stringify(hmac);
  } else {
    const g = (str: string) =>
        new Uint8Array(
          [...unescape(encodeURIComponent(str))].map((c) => c.charCodeAt(0))
        ),
      k = g(key),
      m = g(message),
      c = await window.crypto.subtle.importKey(
        "raw",
        k,
        { name: "HMAC", hash: "SHA-512" },
        true,
        ["sign"]
      ),
      s = await window.crypto.subtle.sign("HMAC", c, m);
    return btoa(String.fromCharCode(...new Uint8Array(s)));
  }
}

const periodRegex = /\./g;
export const getViewQueryParamFromTemplate = (template: string) => {
  template = template.replace("templates/", "");

  const periods = template.match(periodRegex);

  let search: string;

  if ((periods?.length || 0) > 1) {
    const templateParts = template.split(".");
    // slice(1, -1) returns array without first and last element
    search = templateParts.slice(1, -1).join(".");
    return search;
  } else {
    search = template;
    return search.split(".")[0];
  }
};

export const getSuffixFromTemplate = (template: string): string => {
  const periods = template.match(periodRegex) || [];
  let search: string;

  if (periods.length > 1) {
    const templateParts = template.split(".");
    // slice(1, -1) returns array without first and last element
    search = templateParts.slice(1, -1).join(".");
    return search;
  } else {
    // there is no suffix for the template
    return "";
  }
};

export const expectedString = (expected: string) => {
  const expectedList = expected.split(",");
  const diff = expectedList.length - 3;
  return expectedList.length > 3
    ? [
        ...expectedList.slice(0, 3),
        `or ${diff} other${diff > 1 ? "s" : ""}`,
      ].join(", ")
    : expectedList.join(", ");
};

export async function waitForNestedWindowObject(
  obj: string,
  key: string,
  fn: CallableFunction,
  options: { intervalCalls: number; windowObjectTimeoutInterval: number },
  ...args: any[]
) {
  return new Promise((resolve) => {
    if (window && window[obj as any] && window[obj as any][key as any]) {
      fn(...args);
      resolve(undefined);
    } else {
      const waitForNestedWindowObjectInterval = setInterval(() => {
        if (window && window[obj as any] && window[obj as any][key as any]) {
          clearInterval(waitForNestedWindowObjectInterval);
          fn(...args);
          resolve(undefined);
        }
        // If we don't have a Shopify object by 200ms, bail out
        else if (
          options.intervalCalls >=
          200 / options.windowObjectTimeoutInterval
        ) {
          clearInterval(waitForNestedWindowObjectInterval);
          resolve(undefined);
        }
        options.intervalCalls++;
      }, options.windowObjectTimeoutInterval);
    }
  });
}

export async function waitForWindowObject(
  obj: string,
  fn: CallableFunction,
  options: { intervalCalls: number; windowObjectTimeoutInterval: number },
  ...args: any[]
) {
  return new Promise((resolve) => {
    if (window && window[obj as any]) {
      fn(...args);
      resolve(undefined);
    } else {
      const waitForWindowObjectInterval = setInterval(() => {
        if (window && window[obj as any]) {
          clearInterval(waitForWindowObjectInterval);
          fn(...args);
          resolve(undefined);
        }
        // If we don't have a Shopify object by 200ms, bail out
        else if (
          options.intervalCalls >=
          200 / options.windowObjectTimeoutInterval
        ) {
          clearInterval(waitForWindowObjectInterval);
          resolve(undefined);
        }
        options.intervalCalls++;
      }, options.windowObjectTimeoutInterval);
    }
  });
}

export function getIdIndex(idHex: string, entityId: string): number {
  if (idHex == null || entityId == null) {
    throw new Error("Input parameters idHex and entityId must not be null.");
  }

  const combinedString = idHex + entityId;
  let hash = 0;
  const c1 = 0xcc9e2d51;
  const c2 = 0x1b873593;

  for (let i = 0; i < combinedString.length; i++) {
    let k1 = combinedString.charCodeAt(i);
    k1 = (k1 * c1) & 0xffffffff;
    k1 = (k1 << 15) | (k1 >>> 17); // ROTL32
    k1 = (k1 * c2) & 0xffffffff;

    hash ^= k1;
    hash = (hash << 13) | (hash >>> 19); // ROTL32
    hash = (hash * 5 + 0xe6546b64) & 0xffffffff;
  }

  hash ^= combinedString.length;
  hash ^= hash >>> 16;
  hash = (hash * 0x85ebca6b) & 0xffffffff;
  hash ^= hash >>> 13;
  hash = (hash * 0xc2b2ae35) & 0xffffffff;
  hash ^= hash >>> 16;

  return Math.abs(hash) % 100;
}

export function filterEvaluation(options: {
  node: ExpressionNodeQueryType;
  actual: string;
}): PluginTrafficMatchResponse {
  const actual = options.actual;
  const { key, value, filter, type } = options.node;
  const label =
    type === ("cookie" || "url" || "utm") ? `${type} '${key}'` : type;
  switch (filter) {
    case "equals":
      return {
        matched: actual === value,
        by: `because the ${label} equals '${value}'`,
      };
    case "startsWith":
      return {
        matched: actual.startsWith(value),
        by: `because the ${label} starts with '${value}'`,
      };
    case "endsWith":
      return {
        matched: actual.endsWith(value),
        by: `because the ${label} ends with '${value}'`,
      };
    case "doesNotEqual":
      return {
        matched: actual !== value,
        by: `because the ${label} does not equal '${value}'`,
      };
    case "doesNotContain":
      return {
        matched: !actual.includes(value),
        by: `because the ${label} does not contain '${value}'`,
      };
    case "doesNotStartWith":
      return {
        matched: !actual.startsWith(value),
        by: `because the ${label} does not start with '${value}'`,
      };
    case "doesNotEndWith":
      return {
        matched: !actual.endsWith(value),
        by: `because the ${label} does not end with '${value}'`,
      };
    case "isNull":
      return {
        matched: !actual,
        by: `because ${label} does not exist`,
      };
    case "isNotNull":
      return {
        matched: !!actual,
        by: `because ${label} exists`,
      };
    case "contains":
      return {
        matched: actual.includes(value),
        by: `because the ${label} contains '${value}'`,
      };
    case "regex": {
      const regex = new RegExp(value);
      return {
        matched: regex.test(actual),
        by: `because the ${label} matches the regex '${value}'`,
      };
    }
    default:
      return {
        matched: false,
        by: `because ${label} did not ${filter} '${value}'`,
      };
  }
}
